// Which look a page shows: "Bold" (the default since 8 Oct 2026), "Clinical" (code value 'ward'), "Playful" or "Theatre". Pure rules
// and guarded storage, unit-tested in look.test.ts; the store and the switch build on them.
//
// - A battle's look comes from its snapshot or its public info (look, lookLocked). A battle without a look is bold.
// - Staff pages without a battle use the global setting (RoundsSettings.look from GET /studio/settings) when known.
// - A preview override (?look=playful, ?look=clinical, ?look=bold or ?look=theatre, or the switch) is remembered on this device and
//   applies wherever the battle's look is not locked. A locked look (frozen in the Battle builder when the battle was
//   made) always wins.
import type { Look } from '@shared/types';
import { DEFAULT_LOOK, LOOKS, isLook } from '@shared/look';

export type { Look } from '@shared/types';
export { DEFAULT_LOOK, LOOKS, isLook };

/** What people see: the code value 'ward' is labelled "Clinical". */
export const LOOK_LABEL: Record<Look, string> = { bold: 'Bold', ward: 'Clinical', playful: 'Playful', theatre: 'Theatre' };
/** The browser bar colour for each look (the page ground; Theatre keeps Bold's ground, without the dots). */
export const THEME_COLOR: Record<Look, string> = { bold: '#DDF5F1', ward: '#F4F7F6', playful: '#EEF9F5', theatre: '#DDF5F1' };

/** localStorage keys (every read and write is guarded: private windows, blocked storage, Instagram's browser). */
export const PREVIEW_KEY = 'rounds.look.preview';
export const BATTLES_KEY = 'rounds.look.battles';
export const SETTINGS_KEY = 'rounds.look.settings';
/** How many battles' looks this device remembers (newest kept). */
export const BATTLES_KEPT = 8;

/** What a battle says about its look: its snapshot or GET /battles/{code}/public. */
export interface BattleLook { look?: Look; lookLocked?: boolean }
/** The global setting (GET /studio/settings, staff only). */
export interface SettingsLook { look?: Look; lookFrozen?: boolean }
export interface LookInputs {
  /** The battle this page shows; undefined when the page has none (or it is not known yet). */
  battle?: BattleLook | null;
  settings?: SettingsLook | null;
  /** This device's preview override. */
  preview?: Look | null;
}
export interface ResolvedLook {
  /** What to show. */
  look: Look;
  /** What it would be without the preview. */
  base: Look;
  /** The battle's look is frozen: no preview applies and the switch is hidden from players. */
  locked: boolean;
  /** A preview override is set on this device and is in force here. */
  previewing: boolean;
}

/** The rule in one place. */
export function resolveLook({ battle, settings, preview }: LookInputs): ResolvedLook {
  const locked = battle?.lookLocked === true;
  const fromBattle = battle?.look; const fromSettings = settings?.look;
  const base: Look = battle ? (isLook(fromBattle) ? fromBattle : DEFAULT_LOOK) : isLook(fromSettings) ? fromSettings : DEFAULT_LOOK;
  const p = isLook(preview) ? preview : undefined;
  if (p !== undefined && !locked) return { look: p, base, locked, previewing: true };
  return { look: base, base, locked, previewing: false };
}

/** ?look= in a page link: a look (or "clinical") starts a preview; off, auto, none, default, clear or reset ends it. */
export type LookParam = { kind: 'set'; look: Look } | { kind: 'clear' } | { kind: 'none' };
const CLEAR_WORDS = new Set(['off', 'auto', 'none', 'default', 'clear', 'reset']);
export function lookParam(search: string): LookParam {
  let v: string | undefined;
  try { v = new URLSearchParams(search).get('look')?.trim().toLowerCase(); } catch { v = undefined; }
  if (!v) return { kind: 'none' };
  if (isLook(v)) return { kind: 'set', look: v };
  if (v === 'clinical') return { kind: 'set', look: 'ward' };
  if (CLEAR_WORDS.has(v)) return { kind: 'clear' };
  return { kind: 'none' };
}

/** The battle a page names in its path (/j/CODE, /stage/CODE, /host/CODE, /ops/CODE, /terms/CODE) or its ?c= (the game). */
export function codeFromLocation(pathname: string, search = ''): string | undefined {
  const m = /^\/(?:j|stage|host|ops|terms)\/([A-Za-z0-9]{3,12})\/?$/.exec(pathname);
  if (m) return m[1].toUpperCase();
  let c: string | null = null;
  try { c = new URLSearchParams(search).get('c'); } catch { c = null; }
  return c && /^[A-Za-z0-9]{3,12}$/.test(c) ? c.toUpperCase() : undefined;
}
/** MedSnapp's own pages (Battle builder, host, ops): they place the switch themselves and always show it. */
export const isStaffPath = (pathname: string) => /^\/(studio|builder|host|ops|staff|account)(\/|$)/.test(pathname);
/** The stage view (an OBS browser source): the preview switch there hides itself when the mouse is still. */
export const isStagePath = (pathname: string) => /^\/stage(\/|$)/.test(pathname);

// ---------------------------------------------------------------------------------------------------------------
// Storage, always guarded. `store` is any Storage-like object (localStorage in the app); undefined reads as empty.
// ---------------------------------------------------------------------------------------------------------------
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined;
const safe = <T>(fn: () => T): T | undefined => { try { return fn(); } catch { return undefined; } };
/** localStorage, or undefined where touching it throws. */
export const deviceStore = (): Store => safe(() => (typeof window !== 'undefined' ? window.localStorage : undefined));

export function readPreview(store: Store): Look | undefined {
  const v = safe(() => store?.getItem(PREVIEW_KEY));
  return isLook(v) ? v : undefined;
}
export function writePreview(store: Store, look: Look | undefined): void {
  safe(() => (look ? store?.setItem(PREVIEW_KEY, look) : store?.removeItem(PREVIEW_KEY)));
}

type BattleCache = Record<string, BattleLook & { at: number }>;
function readJson<T>(store: Store, key: string): T | undefined {
  const raw = safe(() => store?.getItem(key));
  if (!raw) return undefined;
  const v = safe(() => JSON.parse(raw) as unknown);
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as T) : undefined;
}
const cleanBattle = (v: unknown): BattleLook | undefined => {
  if (!v || typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  const out: BattleLook = {};
  if (isLook(o.look)) out.look = o.look;
  if (o.lookLocked === true) out.lookLocked = true;
  return out;
};
/** The looks this device has seen, by battle code. */
export function readBattleLooks(store: Store): Record<string, BattleLook> {
  const raw = readJson<BattleCache>(store, BATTLES_KEY) ?? {};
  const out: Record<string, BattleLook> = {};
  for (const [code, v] of Object.entries(raw)) { const b = cleanBattle(v); if (b && /^[A-Z0-9]{3,12}$/.test(code)) out[code] = b; }
  return out;
}
/** Remembers a battle's look so the next visit opens in it before the network answers. Keeps the newest few. */
export function rememberBattleLook(store: Store, code: string, info: BattleLook, now = Date.now()): void {
  const c = code.toUpperCase();
  if (!/^[A-Z0-9]{3,12}$/.test(c)) return;
  const raw = readJson<BattleCache>(store, BATTLES_KEY) ?? {};
  const prev = raw[c];
  const next: BattleLook & { at: number } = { ...cleanBattle(info), at: now };
  if (prev && prev.look === next.look && Boolean(prev.lookLocked) === Boolean(next.lookLocked)) return; // nothing new to write
  raw[c] = next;
  const kept = Object.entries(raw)
    .filter(([, v]) => v && typeof v === 'object')
    .sort((a, b) => (Number(b[1].at) || 0) - (Number(a[1].at) || 0))
    .slice(0, BATTLES_KEPT);
  safe(() => store?.setItem(BATTLES_KEY, JSON.stringify(Object.fromEntries(kept))));
}
export function readSettingsLook(store: Store): SettingsLook | undefined {
  const v = readJson<Record<string, unknown>>(store, SETTINGS_KEY);
  if (!v) return undefined;
  return { look: isLook(v.look) ? v.look : undefined, lookFrozen: v.lookFrozen === true };
}
export function rememberSettingsLook(store: Store, s: SettingsLook): void {
  safe(() => store?.setItem(SETTINGS_KEY, JSON.stringify({ look: isLook(s.look) ? s.look : DEFAULT_LOOK, lookFrozen: s.lookFrozen === true })));
}

/** Shows the look: sets <html data-look> (the bold, playful and theatre stylesheets key off it) and the browser bar colour. */
export function applyLook(look: Look, doc: Document | undefined = typeof document !== 'undefined' ? document : undefined): void {
  if (!doc) return;
  const root = doc.documentElement;
  if (root.dataset.look !== look) root.dataset.look = look;
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta && meta.getAttribute('content') !== THEME_COLOR[look]) meta.setAttribute('content', THEME_COLOR[look]);
}
